import { inject, Injectable } from '@angular/core';
import {
  MapboxIsochroneService,
  IsochroneParams,
} from './mapbox-isochrone.service';
import { LeafletMapboxService } from '../leaflet-mapbox.service';
import { MapStore, IdentifiableLayer } from '../../stores/map/map.state';
import { POI } from '../../models/data-layer-type';
import { WidgetStore } from '../../stores/grid/widget.state';
import { ColorsetStore } from '../../stores/colorset/colorset.state';

@Injectable({
  providedIn: 'root',
})
export class PoiIsochroneService {
  private readonly isochroneApi = inject(MapboxIsochroneService);
  private readonly mapboxService = inject(LeafletMapboxService);
  private readonly mapStore = inject(MapStore);
  private readonly widgetStore = inject(WidgetStore);
  private readonly colorsetStore = inject(ColorsetStore);

  private pendingIds = new Set<string>();

  /**
   * Toggles the isochrone layer for a specific POI.
   *
   * @param {POI} poi - The POI object.
   * @param {IsochroneParams} params - Optional isochrone parameters.
   * @return {Promise<void>}
   */
  async toggleIsochrone(poi: POI, params: IsochroneParams = {}) {
    const id = poi.key;
    if (this.isIsochroneActive(id)) {
      this.removeIsochrone(id);
    } else {
      await this.addIsochrone(poi, params);
    }
  }

  /**
   * Toggles the isochrone layer for a specific Feature (from GeoJSON).
   *
   * @param {any} feature - The GeoJSON feature object.
   * @param {IsochroneParams} params - Optional isochrone parameters.
   * @return {Promise<void>}
   */
  async toggleIsochroneForFeature(feature: any, params: IsochroneParams = {}) {
    const id = feature.properties?.key || feature.id;
    if (this.pendingIds.has(id)) return;

    this.pendingIds.add(id);
    try {
      if (this.isIsochroneActive(id)) {
        this.removeIsochrone(id);
      } else {
        const coordinates = feature.geometry?.coordinates;
        if (Array.isArray(coordinates) && coordinates.length >= 2) {
          await this.addIsochroneForPoint(
            id,
            coordinates[0],
            coordinates[1],
            params
          );
        }
      }
    } finally {
      this.pendingIds.delete(id);
    }
  }

  /**
   * Adds an isochrone layer for a POI to the map.
   *
   * @param {POI} poi - The POI object.
   * @param {IsochroneParams} params - Optional isochrone parameters.
   * @return {Promise<void>}
   */
  async addIsochrone(poi: POI, params: IsochroneParams = {}): Promise<void> {
    const lng = poi.geoPositionX;
    const lat = poi.geoPositionY;

    if (lng === undefined || lat === undefined) {
      console.warn('POI coordinates are missing', poi);
      return;
    }

    const id = poi.key;
    await this.addIsochroneForPoint(id, lng, lat, params);
  }

  /**
   * Adds an isochrone layer for a specific point to the map.
   *
   * @param {string} id - A unique identifier for the isochrone.
   * @param {number} lng - Longitude.
   * @param {number} lat - Latitude.
   * @param {IsochroneParams} params - Optional isochrone parameters.
   * @return {Promise<void>}
   */
  async addIsochroneForPoint(
    id: string,
    lng: number,
    lat: number,
    params: IsochroneParams = {}
  ) {
    try {
      const selected = this.widgetStore.selected();
      const config = selected?.widget?.map;

      const profile = params.profile || config?.isochroneProfile || 'walking';
      const minutes =
        (params.minutes?.length ? params.minutes : undefined) ||
        (config?.isochroneMinutes?.length ? config.isochroneMinutes : undefined) ||
        [15, 30, 45];
      const mergedParams: IsochroneParams = {
        profile,
        minutes,
        ...params,
      };

      let colors: string[] = [];
      const colorsetId = config?.isochroneColorset;
      if (colorsetId) {
        const colorset = this.colorsetStore.getById(colorsetId);
        if (colorset) {
          colors = colorset.colors;
        }
      }

      const geojson = await this.isochroneApi.getIsochrone(lng, lat, {
        ...mergedParams,
        contours_colors: colors,
      });

      const layerId = `isochrone-${id}`;
      // Remove old layer if exists
      const oldLayer = this.mapStore.getLayer(layerId);
      const map = this.mapStore.map();
      if (oldLayer && map) {
        map.removeLayer(oldLayer);
      }

      const layer = this.mapboxService.createIsochroneLayer(
        geojson,
        colors,
        {},
        minutes
      ) as unknown as IdentifiableLayer;
      layer.id = layerId;

      if (map) {
        layer.addTo(map);
      }

      this.mapStore.addLayer(layer);
      this.updateConfig(id, true);
    } catch (error) {
      console.error('Failed to add isochrone', error);
    }
  }

  /**
   * Updates (re-fetches and re-renders) the isochrone layer for a specific Feature.
   *
   * @param {any} feature - The GeoJSON feature object.
   * @param {IsochroneParams} params - Optional isochrone parameters.
   * @return {Promise<void>}
   */
  async updateIsochroneForFeature(feature: any, params: IsochroneParams = {}) {
    const id = feature.properties?.key || feature.id;
    const coordinates = feature.geometry?.coordinates;
    if (Array.isArray(coordinates) && coordinates.length >= 2) {
      await this.addIsochroneForPoint(
        id,
        coordinates[0],
        coordinates[1],
        params
      );
    }
  }

  /**
   * Removes an isochrone layer from the map.
   *
   * @param {string} id - The POI ID or key.
   */
  removeIsochrone(id: string) {
    const layerId = `isochrone-${id}`;
    const layer = this.mapStore.getLayer(layerId);
    const map = this.mapStore.map();

    if (layer && map) {
      queueMicrotask(() => {
        map.removeLayer(layer);
        this.mapStore.removeLayer(layerId);
      });
    }
    queueMicrotask(() => {
      this.updateConfig(id, false);
    });
  }

  /**
   * Removes multiple isochrone layers from the map and updates the config once.
   *
   * @param {string[]} ids - The POI IDs or keys to remove.
   */
  removeIsochrones(ids: string[]) {
    if (!ids || ids.length === 0) return;

    ids.forEach(id => {
      const layerId = `isochrone-${id}`;
      const layer = this.mapStore.getLayer(layerId);
      const map = this.mapStore.map();

      if (layer && map) {
        map.removeLayer(layer);
        this.mapStore.removeLayer(layerId);
      }
    });

    this.updateConfigMultiple(ids, false);
  }

  /**
   * Updates the widget configuration with the active isochrone state for multiple IDs.
   *
   * @param {string[]} ids - The POI IDs or keys.
   * @param {boolean} active - Whether the isochrones are active.
   * @private
   */
  private updateConfigMultiple(ids: string[], active: boolean) {
    const selected = this.widgetStore.selected();
    if (!selected || !selected.widget.map) return;

    const widget = this.widgetStore.get(selected.id);
    if (!widget || !widget.widget.map) return;

    const current = widget.widget.map.activeIsochrones || [];
    let next: string[];

    if (active) {
      next = [...new Set([...current, ...ids])];
    } else {
      const toRemove = new Set(ids);
      next = current.filter(key => !toRemove.has(key));
    }

    if (JSON.stringify(current) === JSON.stringify(next)) return;

    const updatedWidget = {
      ...widget.widget,
      map: {
        ...widget.widget.map,
        activeIsochrones: next,
      },
    };

    this.widgetStore.updateWidgetConfigById(selected.id, updatedWidget);
  }

  /**
   * Updates the widget configuration with the active isochrone state.
   *
   * @param {string} id - The POI ID or key.
   * @param {boolean} active - Whether the isochrone is active.
   * @private
   */
  private updateConfig(id: string, active: boolean) {
    const selected = this.widgetStore.selected();
    if (!selected || !selected.widget.map) return;

    // Use the latest widget data from the store
    const widget = this.widgetStore.get(selected.id);
    if (!widget || !widget.widget.map) return;

    const current = widget.widget.map.activeIsochrones || [];

    if (active && current.includes(id)) return;
    if (!active && !current.includes(id)) return;

    const next = active ? [...current, id] : current.filter(key => key !== id);

    const updatedWidget = {
      ...widget.widget,
      map: {
        ...widget.widget.map,
        activeIsochrones: next,
      },
    };

    this.widgetStore.updateWidgetConfigById(selected.id, updatedWidget);
  }

  /**
   * Checks if an isochrone layer is active for a specific ID.
   *
   * @param {string} id - The POI ID or key.
   * @return {boolean} True if active, false otherwise.
   */
  isIsochroneActive(id: string): boolean {
    // Check both MapStore (for immediate feedback) and WidgetStore (for persistence/cross-component)
    const inMapStore = !!this.mapStore.getLayer(`isochrone-${id}`);
    const selected = this.widgetStore.selected();
    const inConfig = !!selected?.widget?.map?.activeIsochrones?.includes(id);

    return inMapStore || inConfig;
  }
}
